import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../_shared/GlassCard';
import { Footer } from '../_shared/Footer';
import { Eye, Users, TrendingUp, Play, Award, Activity, Youtube, Instagram, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';

// ——— Animated Counter Hook ———
const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return [count, ref];
};

// ——— Stat Card ———
const StatCard = ({ icon: Icon, iconColor, label, value, suffix = '', subtext, displayValue }) => {
    const [animatedValue, ref] = useCounter(value);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[180px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight">
                    {displayValue || <>{animatedValue.toLocaleString()}{suffix}</>}
                </p>
                {subtext && <p className="text-white/50 text-sm mt-3">{subtext}</p>}
            </div>
        </GlassCard>
    );
};

// ——— YouTube Reels Data ———
const ytReels = [
    {
        title: 'This is one of the most underrated ways to get clients...',
        badge: 'Top Performer',
        gradient: 'from-red-600 via-rose-500 to-orange-400',
        url: 'https://youtube.com/shorts/y4RKJYLFMUI?si=mkj5DLx5hMs3DAQ7',
        emoji: '🎯',
    },
    {
        title: 'The math behind the $100 Million Dollar launch',
        badge: 'Top Performer',
        gradient: 'from-red-700 via-red-500 to-pink-400',
        url: 'https://youtube.com/shorts/lhEeB22nqv8?si=Pz7_iOezawpFnYCR',
        emoji: '💰',
    },
    {
        title: 'Meta AI vs Manual Targeting',
        badge: 'Top Performer',
        gradient: 'from-blue-600 via-indigo-500 to-violet-500',
        url: 'https://youtube.com/shorts/jTf53IMzvuQ?si=-Ak1m8baLnSPMbYB',
        emoji: '🤖',
    },
    {
        title: 'Use this most underrated way to get insane traffic...',
        badge: 'Top Performer',
        gradient: 'from-emerald-500 via-teal-500 to-cyan-400',
        url: 'https://youtube.com/shorts/mD0VbJBlmlE?si=r6wEkJ-Ii7Xj5toZ',
        emoji: '🚀',
    },
    {
        title: 'The Hormozi Law',
        badge: 'Top Performer',
        gradient: 'from-amber-500 via-orange-500 to-red-500',
        url: 'https://youtube.com/shorts/JlbLYMH9l1M?si=aRYT3FW5iwaVVj4P',
        emoji: '⚡',
    },
];

// ——— Instagram Reels Data ———
const igReels = [
    {
        title: 'Advertising targeting is officially obsolete',
        views: '2K',
        gradient: 'from-purple-600 via-violet-500 to-fuchsia-400',
        url: 'https://www.instagram.com/reel/DWggCQOjk4Z/',
        emoji: '📢',
    },
    {
        title: 'How to raise prices for Your edtech business',
        views: '1.3K',
        gradient: 'from-emerald-500 via-teal-500 to-cyan-400',
        url: 'https://www.instagram.com/reel/DWbYEADDYxM/',
        emoji: '💰',
    },
    {
        title: 'Spy on Your competitors',
        views: '872',
        gradient: 'from-rose-600 via-red-500 to-orange-400',
        url: 'https://www.instagram.com/reel/DWRAADvCi1_/',
        emoji: '🔍',
    },
    {
        title: 'Bad Offer Good Offer',
        views: '615',
        gradient: 'from-blue-600 via-indigo-500 to-violet-500',
        url: 'https://www.instagram.com/reel/DVqet6kk9wD/',
        emoji: '⚖️',
    },
    {
        title: 'Additional Highlight Reel',
        views: 'Viral',
        gradient: 'from-amber-500 via-orange-500 to-red-500',
        url: 'https://www.instagram.com/reel/DV3Sh7Vir_5/',
        emoji: '🔥',
    },
];

// ——— Reel Card Grid ———
const ReelGrid = ({ reels, showViews = false }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
        {reels.map((reel, i) => (
            <a
                key={i}
                href={reel.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 hover:scale-[1.03]"
                style={{ aspectRatio: '9/16' }}
            >
                <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-5xl mb-3 opacity-30 group-hover:opacity-0 transition-opacity duration-300">{reel.emoji}</span>
                </div>
                {/* Title overlay */}
                <div className="absolute top-1/4 left-3 right-3">
                    <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
                        <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{reel.title}</p>
                    </div>
                </div>
                {/* Hover play */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 backdrop-blur-0 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                            <Play size={24} className="text-white ml-1" fill="white" />
                        </div>
                    </div>
                </div>
                {/* Bottom info */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                    <p className="text-white/70 text-xs font-mono">
                        {showViews ? `${reel.views} Views` : reel.badge}
                    </p>
                </div>
                {/* Rank badge */}
                <div className="absolute top-3 left-3">
                    <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">
                        #{i + 1}
                    </span>
                </div>
            </a>
        ))}
    </div>
);
// ——— Screenshot Carousel ———
const ScreenshotCarousel = () => {
    const totalImages = 25;
    const images = Array.from({ length: totalImages }, (_, i) => `/screenshots-sachin/img${i + 1}.jpg`);
    const [currentIndex, setCurrentIndex] = useState(0);

    const nextSlide = () => setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    const prevSlide = () => setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

    return (
        <GlassCard className="relative overflow-hidden group p-4 md:p-6">
            <div className="relative aspect-[9/16] h-[65vh] md:h-[80vh] mx-auto flex items-center justify-center bg-black/40 rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-blue-500/10">
                <img 
                    src={images[currentIndex]} 
                    alt={`Screenshot ${currentIndex + 1}`} 
                    className="w-full h-full object-contain transition-opacity duration-300"
                />
                
                {/* Navigation Controls */}
                <button 
                    onClick={prevSlide}
                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2 md:p-3 rounded-full backdrop-blur-md transition-all border border-white/10 z-10"
                >
                    <ChevronLeft size={24} />
                </button>
                <button 
                    onClick={nextSlide}
                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-2 md:p-3 rounded-full backdrop-blur-md transition-all border border-white/10 z-10"
                >
                    <ChevronRight size={24} />
                </button>

                {/* Progress Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md px-4 py-1.5 rounded-full text-white text-sm font-medium border border-white/20 z-10">
                    {currentIndex + 1} / {images.length}
                </div>
            </div>
            
            {/* Thumbnail Navigation */}
            <div className="flex overflow-x-auto gap-2 mt-6 pb-2 snap-x hide-scrollbar max-w-4xl mx-auto">
                {images.map((img, idx) => (
                    <button 
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        className={`flex-none w-16 h-24 rounded-xl overflow-hidden border-2 transition-all ${
                            currentIndex === idx ? 'border-purple-500 scale-105 opacity-100 ring-2 ring-purple-500/50' : 'border-transparent opacity-50 hover:opacity-80'
                        }`}
                    >
                        <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>
            <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </GlassCard>
    );
};

// ——— Main Report ———
export default function SachinReport() {
    useEffect(() => {
        const pageTitle = 'Sachin – 30-Day Report | SocialSEO';
        document.title = pageTitle;
        const ensureMeta = (selector, attributes) => {
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            el.setAttribute('content', pageTitle);
        };
        ensureMeta('meta[property="og:title"]', { property: 'og:title' });
        ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
            `}</style>

            {/* Navigation */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight text-purple-300">
                        <span className="text-white">Social</span>SEO
                    </span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">March 2026</span>
                </div>
            </header>

            <div className="relative z-10">
                <main>
                    {/* Hero Header */}
                    <section className="min-h-[50vh] flex flex-col justify-center px-4 pt-24 pb-8">
                        <div className="max-w-6xl mx-auto w-full space-y-2">
                            <p className="text-purple-400 font-bold text-sm uppercase tracking-widest">Social SEO</p>
                            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-white leading-tight">
                                Sachin 30 Day Report
                            </h1>
                            <p className="text-lg text-white/60">Top performing reels in the month of March</p>
                        </div>
                    </section>

                    {/* ═══ YOUTUBE SECTION ═══ */}
                    <section className="pb-16 px-4">
                        <div className="container mx-auto max-w-6xl">
                            {/* YouTube Section Header */}
                            <div className="flex items-center gap-3 mb-8">
                                <div className="bg-red-500/20 text-red-400 p-2.5 rounded-xl">
                                    <Youtube size={22} />
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-white">YouTube Performance</h2>
                            </div>

                            {/* YouTube KPIs */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                                <StatCard
                                    icon={Eye}
                                    iconColor="bg-red-500/20 text-red-300"
                                    label="Total Views Gained"
                                    value={21949}
                                    subtext="21,949 times watched in March"
                                />
                                <StatCard
                                    icon={Users}
                                    iconColor="bg-purple-500/20 text-purple-300"
                                    label="Follower Growth"
                                    value={64}
                                    suffix="+"
                                    subtext="Gained in March"
                                />
                                <StatCard
                                    icon={TrendingUp}
                                    iconColor="bg-blue-500/20 text-blue-300"
                                    label="Subscribers Journey"
                                    value={627}
                                    subtext="Started March at 563 total"
                                />
                                <StatCard
                                    icon={Award}
                                    iconColor="bg-pink-500/20 text-pink-300"
                                    label="Total Viral Reels"
                                    value={5}
                                    subtext="Top performing content"
                                />
                            </div>

                            {/* YouTube Viral Reels */}
                            <div className="mb-6 flex items-center gap-3">
                                <h3 className="text-xl font-bold text-white">5 Viral Reels Spotlight</h3>
                                <span className="bg-red-500/20 text-red-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-red-500/30">
                                    YouTube Shorts
                                </span>
                            </div>
                            <ReelGrid reels={ytReels} showViews={false} />
                        </div>
                    </section>

                    {/* Divider */}
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="border-t border-white/10" />
                    </div>

                    {/* ═══ INSTAGRAM SECTION ═══ */}
                    <section className="py-16 px-4">
                        <div className="container mx-auto max-w-6xl">
                            {/* Instagram Section Header */}
                            <div className="flex items-center gap-3 mb-8">
                                <div className="bg-purple-500/20 text-purple-400 p-2.5 rounded-xl">
                                    <Instagram size={22} />
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-white">Instagram Performance</h2>
                            </div>

                            {/* Instagram KPIs */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
                                <StatCard
                                    icon={Users}
                                    iconColor="bg-purple-500/20 text-purple-300"
                                    label="Follower Growth"
                                    value={525}
                                    suffix="+"
                                    subtext="Started March at 3,282 → Now 3,807"
                                />
                                <StatCard
                                    icon={Activity}
                                    iconColor="bg-pink-500/20 text-pink-300"
                                    label="Organic Interactions"
                                    value={836}
                                    subtext="Out of 4,401 total interactions (Non-Ads)"
                                />
                                <StatCard
                                    icon={Eye}
                                    iconColor="bg-amber-500/20 text-amber-300"
                                    label="Viral Reel Views"
                                    value={4787}
                                    suffix="+"
                                    subtext="Accumulated from top reels"
                                />
                            </div>

                            {/* Instagram Viral Reels */}
                            <div className="mb-6 flex items-center gap-3">
                                <h3 className="text-xl font-bold text-white">5 Viral Reels Spotlight</h3>
                                <span className="bg-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-purple-500/30">
                                    Instagram Reels
                                </span>
                            </div>
                            <ReelGrid reels={igReels} showViews={true} />
                        </div>
                    </section>

                    {/* Divider */}
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="border-t border-white/10" />
                    </div>

                    {/* ═══ EVIDENCE GALLERY SECTION ═══ */}
                    <section className="py-16 px-4">
                        <div className="container mx-auto max-w-6xl">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="bg-blue-500/20 text-blue-400 p-2.5 rounded-xl">
                                    <ImageIcon size={22} />
                                </div>
                                <h2 className="text-2xl md:text-3xl font-bold text-white">Campaign Evidence & Analytics</h2>
                            </div>
                            
                            <ScreenshotCarousel />
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </div>
    );
}
